import { memo, useMemo, useRef, useState } from "react";
import { formatTimecode, timecodeAt } from "@infoflyt/shared/timecode";
import { erPickup } from "@infoflyt/shared/synk";
import { useSession } from "../state/SessionProvider";
import { Wheel } from "../components/Wheel";
import { confirm as hapticConfirm, tap as hapticTap } from "../components/haptics";
import type { Deltaker, SuSpoersmaal } from "../state/types";

/**
 * SU og synk: hva som skal spørres om, og hva som ble spurt.
 *
 * SU er stand up — deltakeren står i gangen og svarer på tre raske spørsmål.
 * En SYNK er det lange intervjuet. To situasjoner, én liste. Se
 * `docs/01-domene.md`.
 *
 * TO TILSTANDER, IKKE ÉN FLATE. Først velger man hvem man skal snakke med,
 * så ser man spørsmålene. Det er ikke bare navigasjon: et intervju har en
 * begynnelse og en slutt, og flaten skal si hvilken av dem man står i.
 * Velgeren ligger midt i feltet, som den tomme hurtiglogg-siden — det er
 * tydelig at flaten venter på at du gjør noe.
 *
 * ÉN POST PER SYNK. Første avhuking setter en markør med etiketten SYNK, og
 * spørsmålene legges inn i teksten etter hvert som de stilles. Tidskoden er
 * synkens begynnelse — det er den man leter etter i klipp.
 *
 * HAKEN ER IKKE EN TILSTAND. Et spørsmål er stilt hvis det finnes et svar
 * hvis POST LEVER. Derfor er lista alltid i takt med loggen: slettes
 * SYNK-posten på Logg-skjermen, faller hele synken tilbake til ustilt. Og
 * derfor paret (spørsmål, deltaker) og ikke spørsmålet alene — «Hvordan har
 * du det?» til ALI er ikke det samme som til BEN.
 */

/** Spørsmål uten deltaker. De stilles til alle, og ligger øverst. */
const GENERELL = null;

export const SuScreen = memo(function SuScreen({
  deltakere, spoersmaal, onNyttSpoersmaal, stiltEllers,
}: {
  readonly deltakere: readonly Deltaker[];
  readonly spoersmaal: readonly SuSpoersmaal[];
  onNyttSpoersmaal(deltakerId: string | null, tekst: string): void;
  /**
   * Parene «spoersmaal·deltaker» som er stilt ET ANNET STED.
   *
   * ET SPOERSMAAL SOM ER STILT ER STILT FOR ALLE — avgjort 25.10.2026. Har
   * reporteren huket av fra nettflata, skal spoersmaalet ut av denne lista
   * ogsaa; ellers faar deltakeren det to ganger. Hentes av `su.synkroniser`.
   */
  readonly stiltEllers?: readonly string[];
}): JSX.Element {
  const { entries, svar, anchor, spec, svarPaa, angreSvar, skrivSvar } = useSession();

  /** Hvem hjulet står på. Ingen er valgt før «Velg deltaker» trykkes. */
  const [pekerPaa, setPekerPaa] = useState(0);
  const [iIntervju, setIIntervju] = useState<Deltaker | null>(null);
  /*
   * Spørsmålene som er huket av I DETTE intervjuet.
   *
   * De blir stående i lista så lenge intervjuet varer — man skal kunne angre
   * et feiltrykk. Ved «Ferdig med intervju» tømmes settet, og neste gang man
   * møter samme person er de borte: de er alt stilt.
   */
  const [iDenneSynken, setIDenneSynken] = useState<ReadonlySet<string>>(new Set());
  const [skriver, setSkriver] = useState(false);
  const [utkast, setUtkast] = useState("");
  /**
   * Svartekst under redigering, etter svar-id.
   *
   * Skrives inn mens man sitter der, og lagres naar feltet mister fokus.
   * Lagring ved hvert tastetrykk ville skrevet til basen tjue ganger paa en
   * setning.
   */
  const [svarutkast, setSvarutkast] = useState<Record<string, string>>({});
  /**
   * Svaret lagres MENS DU SKRIVER, ikke bare naar feltet mister fokus.
   *
   * Paa en telefon er blur ikke noe man kan stole paa: man swiper til en
   * annen side, legger telefonen i lomma eller faar en samtale, og feltet
   * mister aldri fokus paa den maaten en nettleser venter. Da sto svaret paa
   * skjermen og ingen steder ellers. Rettet 25.10.2026.
   */
  const svartimer = useRef<Record<string, number>>({});

  /*
   * Oppslaget: (spørsmål, deltaker) → synken det ble stilt i.
   *
   * Et svar teller bare hvis POSTEN LEVER. `entries` inneholder ikke
   * tombstoner, så et oppslag mot den er hele koblingen.
   *
   * Tidskoden som vises er SYNKENS, ikke spørsmålets. Det er posten som
   * finnes i materialet, og alle spørsmålene i samme synk deler den.
   */
  const stilt = useMemo(() => {
    const levende = new Map(entries.map((e) => [e.id, e]));
    const ut = new Map<string, { frames: number; svarId: string; svar: string }>();
    for (const v of svar) {
      const post = levende.get(v.entryId);
      if (post === undefined) continue;
      ut.set(`${v.spoersmaalId}·${v.deltakerId}`, {
        frames: anchor === null ? 0 : timecodeAt(anchor, post, spec).frames,
        svarId: v.id,
        svar: v.svar,
      });
    }
    return ut;
  }, [entries, svar, anchor, spec]);

  /*
   * STILT ET STED, STILT OVERALT.
   *
   * `stilt` over er appens EGNE haker, med tidskode fra posten de staar i.
   * Dette er resten: det reporteren og de andre har huket av. De har ingen
   * tidskode her — posten staar i en annen logg, og den hoerer ikke paa
   * denne skjermen. Men spoersmaalet skal ut av lista.
   */
  const stiltEtSted = useMemo(() => new Set(stiltEllers ?? []), [stiltEllers]);

  /*
   * Lista for den man sitter med.
   *
   * Et spørsmål som ALT er stilt til denne personen er ute — det er hele
   * poenget med «Ferdig med intervju»: ingen skal få samme spørsmål to
   * ganger. Unntaket er det man huket av akkurat nå, som må bli stående så
   * lenge intervjuet varer.
   *
   * Det er PARET som faller bort, ikke spørsmålet. «Hvordan har du det?»
   * stilt til ALI står fortsatt på BEN sin liste.
   */
  const aktuelle = useMemo(() => {
    const d = iIntervju;
    if (d === null) return { generelle: [], egne: [], pickups: [] };
    const erStilt = (q: SuSpoersmaal): boolean =>
      stilt.has(`${q.id}·${d.id}`) || stiltEtSted.has(`${q.id}·${d.id}`);
    const med = (q: SuSpoersmaal): boolean => iDenneSynken.has(q.id) || !erStilt(q);

    /*
     * PICKUPS: det som har staatt for lenge.
     *
     * Et spoersmaal som ikke er stilt et doegn etter at det ble skrevet
     * angaar som regel en tidligere historie som er fulgt opp. Det er noe
     * annet enn det som er aktuelt naa, og da skal det staa for seg.
     * Regelen ligger i packages/shared/synk, saa appen og nettflata aldri
     * kan bli uenige om hva en pickup er. Bestilt 26.10.2026.
     *
     * Tiden leses ÉN gang. Leste hver linje sin egen `Date.now()`, kunne to
     * spoersmaal skrevet i samme sekund havnet i hver sin gruppe.
     */
    const naa = Date.now();
    const pickup = (q: SuSpoersmaal): boolean =>
      !iDenneSynken.has(q.id)
      && erPickup(
        { skrevetMs: q.skrevetMs, generelt: q.deltakerId === GENERELL },
        erStilt(q),
        naa,
      );

    /* De generelle staar uroert: de stilles hver gang man synker, og hoerer
       til lista for godt. Bare personens EGNE kan bli pickups. */
    const med1 = spoersmaal.filter(med);
    const mine = med1.filter((q) => q.deltakerId === d.id);
    return {
      generelle: med1.filter((q) => q.deltakerId === GENERELL),
      egne: mine.filter((q) => !pickup(q)),
      pickups: mine.filter(pickup),
    };
  }, [spoersmaal, iIntervju, iDenneSynken, stilt, stiltEtSted]);

  const veksle = (q: SuSpoersmaal, d: Deltaker): void => {
    if (stilt.has(`${q.id}·${d.id}`)) {
      hapticTap();
      angreSvar(q.id, d.id, q.tekst);
      setIDenneSynken((prev) => {
        const neste = new Set(prev);
        neste.delete(q.id);
        return neste;
      });
      return;
    }
    hapticConfirm();
    svarPaa(q.id, d, q.tekst);
    setIDenneSynken((prev) => new Set(prev).add(q.id));
  };

  const tilVelgeren = (): void => {
    setIDenneSynken(new Set());
    setIIntervju(null);
    setSkriver(false);
    setUtkast("");
  };

  const lagre = (): void => {
    const tekst = utkast.trim();
    setUtkast("");
    setSkriver(false);
    if (tekst.length === 0 || iIntervju === null) return;
    onNyttSpoersmaal(iIntervju.id, tekst);
  };

  /*
   * Ikke en blank flate. Blankt ser ut som en feil, og folk begynner å ringe
   * — se docs/12-ui-feltapp.md. Teksten sier hvor deltakerne kommer fra.
   */
  if (deltakere.length === 0) {
    return (
      <div className="surface" style={{ height: "100%", display: "flex" }}>
        <div className="empty">
          <p className="empty__text">
            Ingen deltakere ennå.
            <br />
            Redaktør legger dem inn på nettsiden.
          </p>
        </div>
      </div>
    );
  }

  /* ---- Velgeren --------------------------------------------------------
   *
   * Samme plass og samme tyngdepunkt som den tomme hurtiglogg-siden: midt i
   * feltet, med én ting å gjøre.
   */
  if (iIntervju === null) {
    const n = deltakere.length;
    const i = ((pekerPaa % n) + n) % n;
    const naa = deltakere[i];
    if (naa === undefined) return <div className="surface" />;
    // Samme retning som hjulene på Timer: å dra nedover går framover i lista.
    const fork = (steg: number): string =>
      deltakere[(((i + steg) % n) + n) % n]?.forkortelse ?? "";
    const forrige = fork(1);
    const neste = fork(-1);

    return (
      <div className="surface" style={{ height: "100%", display: "flex" }}>
        <div className="empty">
          <div className="su__hjul">
            <Wheel
              label="DELTAKER"
              value={naa.forkortelse}
              prev={forrige}
              next={neste}
              prev2={fork(2)}
              next2={fork(-2)}
              active
              /*
               * Mer enn det dobbelte av tidskodens hakk.
               *
               * Tidskoden har seksti verdier og maa kunne spinnes; her er det
               * en haandfull navn, og hvert hakk er et valg. Med 20 px hoppet
               * hjulet videre bare av at fingeren gled i det den slapp.
               */
              notchPx={52}
              onFocus={() => undefined}
              onStep={(d) => setPekerPaa((p) => p + d)}
            />
            <span className="su__hjulnavn">{naa.navn}</span>
          </div>
          <button
            type="button"
            className="su__start"
            onClick={() => { hapticTap(); setIIntervju(naa); }}
          >
            Velg deltaker
          </button>
        </div>
      </div>
    );
  }

  /* ---- Intervjuet ------------------------------------------------------ */

  const rad = (q: SuSpoersmaal, d: Deltaker): JSX.Element => {
    const treff = stilt.get(`${q.id}·${d.id}`);
    /*
     * SVARET STAAR UTENFOR KNAPPEN.
     *
     * En textarea inne i en button er ugyldig, og trykket ville gaatt til
     * begge — man ville angret haken i det man satte markoeren i feltet.
     */
    return (
      <div className="su__linje" key={q.id}>
        <button
          type="button"
          className={`su__rad${treff === undefined ? "" : " su__rad--stilt"}`}
          onClick={() => veksle(q, d)}
          aria-pressed={treff !== undefined}
          aria-label={treff === undefined
            ? `Marker som stilt: ${q.tekst}`
            : `Angre: ${q.tekst}`}
        >
          <span className="su__hake" aria-hidden="true">{treff === undefined ? "" : "✓"}</span>
          {/*
            * OPPTAKSDAGEN SPOERSMAALET ER FRA.
            *
            * Regi bruker den til aa hjelpe deltakeren aa huske: «dette er fra
            * dag 10, da dere var i hagen». Bestilt 26.10.2026.
            *
            * BARE PAA DE PERSONLIGE. Et generelt spoersmaal er evergreen —
            * det stilles hver gang man synker — og «fra dag 0» om «Hvordan
            * har du det?» er ikke en hjelp, det er stoey paa hver linje.
            */}
          {q.deltakerId === GENERELL || q.opptaksdag === null ? null : (
            <span className="su__dag mono">D{q.opptaksdag}</span>
          )}
          <span className="su__tekst">{q.tekst}</span>
          {treff === undefined ? null : (
            <span className="su__tc mono">{formatTimecode(treff.frames, spec)}</span>
          )}
        </button>
        {/*
          * Feltet kommer naar spoersmaalet er huket av, og ikke foer: et svar
          * paa noe man ikke stilte er ikke en ting. Det er samme regel som
          * paa reporterens flate.
          */}
        {treff === undefined ? null : (
          <textarea
            className="su__svar"
            rows={2}
            value={svarutkast[treff.svarId] ?? treff.svar}
            placeholder="Hva svarte de?"
            aria-label={`Svar på ${q.tekst}`}
            onChange={(e) => {
              const v = e.target.value;
              setSvarutkast((p) => ({ ...p, [treff.svarId]: v }));
              /* Lagres av seg selv naar du tar en pause i skrivingen. */
              window.clearTimeout(svartimer.current[treff.svarId]);
              svartimer.current[treff.svarId] = window.setTimeout(() => {
                if (v !== treff.svar) skrivSvar(treff.svarId, v, q.tekst);
              }, 900);
            }}
            onBlur={() => {
              window.clearTimeout(svartimer.current[treff.svarId]);
              const t = svarutkast[treff.svarId];
              if (t === undefined || t === treff.svar) return;
              skrivSvar(treff.svarId, t, q.tekst);
              setSvarutkast((p) => {
                const n = { ...p };
                delete n[treff.svarId];
                return n;
              });
            }}
          />
        )}
      </div>
    );
  };

  const tomt = aktuelle.generelle.length === 0 && aktuelle.egne.length === 0
    && aktuelle.pickups.length === 0;

  return (
    <div className="surface surface--rull" style={{ height: "100%" }}>
      <div className="su">
        <div className="su__hvem">
          <span className="su__hvem-fork mono">{iIntervju.forkortelse}</span>
          <span className="su__hvem-navn">{iIntervju.navn}</span>
          {/* Veien ut uten å avslutte. Velger man feil person, skal man ikke
              måtte late som intervjuet er gjort for å komme seg vekk. */}
          <button
            type="button"
            className="su__bytt"
            onClick={() => { hapticTap(); tilVelgeren(); }}
          >
            Bytt
          </button>
        </div>

        {tomt ? (
          <span className="su__hint">
            Alle spørsmålene til {iIntervju.forkortelse} er stilt.
          </span>
        ) : null}

        {aktuelle.generelle.length === 0 ? null : (
          <>
            <span className="su__gruppe">Til alle</span>
            {aktuelle.generelle.map((q) => rad(q, iIntervju))}
          </>
        )}

        {aktuelle.egne.length === 0 ? null : (
          <>
            <span className="su__gruppe">{iIntervju.navn}</span>
            {aktuelle.egne.map((q) => rad(q, iIntervju))}
          </>
        )}

        {/*
          * PICKUPS NEDERST, med sin egen tittel og en strek over.
          *
          * De er ikke borte — de skal fortsatt kunne stilles. De staar bare
          * for seg, saa det som er aktuelt i dag leses foerst.
          */}
        {aktuelle.pickups.length === 0 ? null : (
          <>
            <span className="su__gruppe su__gruppe--pickup">Pickups</span>
            {aktuelle.pickups.map((q) => rad(q, iIntervju))}
          </>
        )}

        {skriver ? (
          <div className="su__nytt">
            <textarea
              className="su__felt"
              value={utkast}
              onChange={(e) => setUtkast(e.target.value)}
              placeholder={`Nytt spørsmål til ${iIntervju.forkortelse}`}
              rows={2}
              autoFocus
            />
            <div className="su__nytt-knapper">
              <button
                type="button"
                className="su__avbryt"
                onClick={() => { setUtkast(""); setSkriver(false); }}
              >
                Avbryt
              </button>
              <button type="button" className="su__lagre" onClick={lagre}>
                Legg til
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="su__legg-til" onClick={() => setSkriver(true)}>
            + Nytt spørsmål
          </button>
        )}

        {/*
          Avslutningen SLETTER INGENTING. Spørsmålene forsvinner fra lista
          fordi de ER stilt, og det står i loggen. Slettes SYNK-posten, kommer
          de tilbake. Hadde knappen fjernet selve spørsmålet, ville et
          generelt spørsmål blitt borte for alle de andre deltakerne også.
        */}
        <button
          type="button"
          className="su__ferdig"
          onClick={() => { hapticConfirm(); tilVelgeren(); }}
        >
          Ferdig med intervju
        </button>
      </div>
    </div>
  );
});
